import React, {useEffect, useState} from 'react'
import {Button, Form, Spinner} from 'react-bootstrap'
import axios from 'axios'
import {CommonLoading} from '../../../../Utils/commonLoading'
import toaster from '../../../../Utils/toaster'

const API_URL = process.env.REACT_APP_API_URL

type SettingsResponse = {
  enabled: boolean
  trigger72Hours: boolean
  trigger48Hours: boolean
  trigger24Hours: boolean
}

type UnfilledAssignmentEmailSettingsProps = {
  requesterId: string
}

export function UnfilledAssignmentEmailSettings({
  requesterId,
}: UnfilledAssignmentEmailSettingsProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [trigger72Hours, setTrigger72Hours] = useState(false)
  const [trigger48Hours, setTrigger48Hours] = useState(false)
  const [trigger24Hours, setTrigger24Hours] = useState(false)
  const [validationMessage, setValidationMessage] = useState('')

  const hasSelectedTrigger = trigger72Hours || trigger48Hours || trigger24Hours

  const loadSettings = async () => {
    if (!requesterId) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = await axios.get<SettingsResponse>(
        `${API_URL}/requester-unfilled-assignment-email-settings/${requesterId}`
      )
      setEnabled(response.data.enabled)
      setTrigger72Hours(response.data.trigger72Hours)
      setTrigger48Hours(response.data.trigger48Hours)
      setTrigger24Hours(response.data.trigger24Hours)
      setValidationMessage('')
    } catch (error: any) {
      toaster('error', error?.response?.data?.message ?? 'Failed to load email notification settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [requesterId])

  const updateEnabled = (checked: boolean) => {
    setEnabled(checked)
    if (!checked) {
      setValidationMessage('')
    }
  }

  const saveSettings = async () => {
    if (enabled && !hasSelectedTrigger) {
      setValidationMessage('Select at least one trigger to save email notification preferences.')
      return
    }

    setSaving(true)
    try {
      await axios.put(`${API_URL}/requester-unfilled-assignment-email-settings/${requesterId}`, {
        enabled,
        trigger72Hours: enabled ? trigger72Hours : false,
        trigger48Hours: enabled ? trigger48Hours : false,
        trigger24Hours: enabled ? trigger24Hours : false,
      })
      setValidationMessage('')
      toaster('success', 'Email notification preferences saved')
      await loadSettings()
    } catch (error: any) {
      const message =
        error?.response?.data?.message ?? 'Failed to save email notification preferences'
      setValidationMessage(message)
      toaster('error', message)
    } finally {
      setSaving(false)
    }
  }

  const renderTrigger = (
    id: string,
    checked: boolean,
    onChange: (checked: boolean) => void,
    title: string,
    subtitle: string
  ) => (
    <label className='d-flex align-items-start gap-4 mb-5 cursor-pointer' htmlFor={id}>
      <Form.Check
        type='checkbox'
        id={id}
        className='mt-1'
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked)
          setValidationMessage('')
        }}
      />
      <span>
        <span className='d-block fw-semibold text-gray-900 fs-6'>{title}</span>
        <span className='d-block text-muted fs-7 mt-1'>{subtitle}</span>
      </span>
    </label>
  )

  if (loading) {
    return (
      <div className='d-flex justify-content-center py-8'>
        <CommonLoading />
      </div>
    )
  }

  return (
    <div>
      <div className='mb-5'>
        <h4 className='fw-bold text-gray-900 mb-2'>Configure Email Alerts</h4>
        <p className='text-muted fs-6 mb-0'>Configure automated email alerts for the user</p>
      </div>

      <div className='card shadow-sm border border-gray-200 mb-5'>
        <div className='card-body p-5'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-start gap-4'>
            <div className='flex-grow-1 pe-md-4'>
              <h5 className='fw-bold text-gray-900 mb-2'>Enable Email For Unfilled Assignment</h5>
              <p className='text-muted fs-6 mb-0' style={{maxWidth: 650}}>
                Configure automated email alerts to the client user when an assignment remains
                unassigned as its scheduled start time approaches.
              </p>
            </div>
            <div className='d-flex align-items-center gap-2 align-self-md-center'>
              {saving && <Spinner animation='border' size='sm' />}
              <Form.Check
                type='switch'
                id='enable-unfilled-assignment-email'
                checked={enabled}
                disabled={saving}
                onChange={(e) => updateEnabled(e.target.checked)}
                className='m-0'
                style={{cursor: saving ? 'not-allowed' : 'pointer'}}
              />
            </div>
          </div>
        </div>
      </div>

      {enabled && (
        <div className='card shadow-sm border border-gray-200 mb-5'>
          <div className='card-body p-5'>
            <div className='d-flex align-items-center gap-3 mb-5'>
              <h5 className='fw-bold text-gray-900 mb-0'>Trigger based on Time left for appointment</h5>
              <span className='badge bg-light-warning text-warning fw-semibold'>Required</span>
            </div>

            {renderTrigger(
              'unfilled-email-72-hours',
              trigger72Hours,
              setTrigger72Hours,
              '72 hours before',
              'Early warning for hard-to-fill assignments.'
            )}
            {renderTrigger(
              'unfilled-email-48-hours',
              trigger48Hours,
              setTrigger48Hours,
              '48 hours before',
              'Standard alert window for unassigned appointments.'
            )}
            {renderTrigger(
              'unfilled-email-24-hours',
              trigger24Hours,
              setTrigger24Hours,
              '24 hours before',
              'Critical alert.'
            )}

            {validationMessage && (
              <div className='text-danger fs-7 fw-semibold mt-2'>{validationMessage}</div>
            )}
          </div>
        </div>
      )}

      <div className='d-flex justify-content-end gap-3'>
        <Button variant='light' disabled={saving} onClick={loadSettings}>
          Cancel
        </Button>
        <Button
          type='button'
          disabled={saving || !requesterId}
          onClick={saveSettings}
          style={{backgroundColor: '#a3a242', borderColor: '#a3a242'}}
        >
          Save Preferences
        </Button>
      </div>
    </div>
  )
}
